import { Button, CircularProgress, Tooltip } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Styles from "../../lib/Styles";
import DragAndDrop from './DragAndDrop';
import APIService from '../../shared/api-service';
import classes from './SelectVINFile.module.scss';
import DisplayReadOnlyField from "./DisplayReadOnlyField";
import { Interweave } from "interweave";
import { AlertDialogContent } from "../../containers/table/EnhancedTable";
import AlertDialog from "../AlertDialog";

interface IVINFile {
    initialValue?: String | undefined;
    selectedVINFileName: string | undefined;
    setSelectedVINFileName: Function;

    initialVINFileGUID?: String | undefined;
    selectedVINFileGUID: string | undefined;
    setSelectedVINFileGUID: Function;

    setListOfWMIs: Function;

    onChange: Function;
    readOnly?: boolean;
}
export default function SelectVINFile(props: IVINFile) {
    const { selectedVINFileName, setSelectedVINFileName,
        selectedVINFileGUID, setSelectedVINFileGUID,
        setListOfWMIs, onChange, readOnly } = props;
    let initialValue = useRef(props.initialValue);
    let initialVINFileGUID = useRef(props.initialVINFileGUID);

    const cx = new Styles(classes).cx;
    const [file, setFile] = useState<any>(null);
    const [error, setError] = useState<any>(null);
    const [uploading, setUploading] = useState<boolean>(false);
    const [selectFileEnabled, setSelectFileEnabled] = useState<boolean>(true);
    const [numLines, setNumLines] = useState<number>(0);
    const [vinFileData, setVINFileData] = useState<string>('');
    const [rawVinFileData, setRawVINFileData] = useState<string>('');
    const [vinFileError, setVINFileError] = useState<string | null>();
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [alertDialogContent, setAlertDialogContent] = useState<AlertDialogContent>();

    let refFileInput: any;

    useEffect(() => {
        if (initialValue.current) {
            setSelectedVINFileName(initialValue.current);
            initialValue.current = undefined;
        }
        if (initialVINFileGUID.current) {
            setSelectedVINFileGUID(initialVINFileGUID.current);
            initialVINFileGUID.current = undefined;
        }
    }, [setSelectedVINFileName, selectedVINFileGUID, setSelectedVINFileGUID])

    useEffect(() => {
        if (!selectedVINFileName || selectedVINFileName === '') {
            setNumLines(0);
            setVINFileData('');
            setFile(null);
            if (refFileInput) {
                refFileInput.files = null;
                refFileInput.value = '';
            }
        }
    }, [selectedVINFileName, refFileInput])

    const handleChange = (event: any) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            handleFileChanged(selectedFile);
        }
    }

    const handleDrop = (files: any) => {
        const selectedFile = files[0];

        if (refFileInput) {
            refFileInput.files = files;
            handleFileChanged(selectedFile);
        }
    }

    // Update UI elements when the file selection changes.
    const handleFileChanged = (selectedFile: any) => {
        readFileContents(selectedFile);
        setFile(selectedFile);
        setSelectedVINFileName(selectedFile.name)
        setError(null);
        setVINFileError(null);
        setSelectedVINFileGUID(null);
        onChange();
    }

    const isValidationError = (data: string): string | null => {
        let retVal = null;

        if (!data) {
            retVal = 'No Content'
        }

        if (!retVal) {
            const dataArray: Array<string> = data.split('\n');
            if (dataArray.length <= 1) {
                retVal = 'No Data';
            }

            if (!retVal) {
                const regex = /[ioqIOQ]/gm;
                for (let i = 1; i < dataArray.length; i++) {
                    const dataLine = dataArray[i];
                    if (dataLine.length > 17) {
                        retVal = 'VIN has more than 17 chars on line ' + ++i;
                        break;
                    } else if (dataLine.length < 17) {
                        retVal = 'VIN has less than 17 chars on line ' + ++i;
                        break;
                    } else if (dataLine.length > 0) {
                        const searchResult = regex.exec(dataLine);
                        if (searchResult !== null) {
                            retVal = 'VIN has invalid char (i, o, or q) on line ' + ++i;
                            break;
                        }
                    }
                }
            }
        }
        return retVal;
    }

    const readFileContents = (file: any): void => {
        if (file?.name) {
            const uploadFile = new File([file], file.name, { type: file.type });
            uploadFile.text().then((data: string) => {
                setRawVINFileData(data);
                // Windows likes to add '\r' in addition to '\n' at the end of a line.  Remove the superfluous '\r'. 
                let dataWithoutCR = data.replaceAll('\r', '');

                // Remove trailing blank lines
                while (dataWithoutCR.length - dataWithoutCR.lastIndexOf('\n') === 1) {
                    dataWithoutCR = dataWithoutCR.substring(0, dataWithoutCR.length - 1)
                }
                const error = isValidationError(dataWithoutCR);
                setVINFileError(error);
                setListOfWMIs('');
                if (error) {
                    setVINFileData(dataWithoutCR);
                } else {
                    // TODO: Update the following line once we have back-end support for translating WMIs to manufacturers.
                    const listOfWMIs = getListOfWMIs(dataWithoutCR);
                    if (setListOfWMIs) {
                        setListOfWMIs(listOfWMIs);
                    }
                    setVINFileData(dataWithoutCR);
                }
            })
        } else {
            setVINFileData('');
            setListOfWMIs('');
        }
    }

    const convertToWMI = (firstThreeChars: string): string => {
        return 'fn(' + firstThreeChars + ')';
    }

    const getListOfWMIs = (data: string): string => {
        let set: { [key: string]: string } = {};
        if (!!data) {
            const dataArray: Array<string> = data.split('\n');
            if (dataArray.length > 1) {
                dataArray.forEach((vin: string, index: number) => {
                    if (index > 0) {
                        const firstThreeChars: string = vin.substr(0, 3);
                        if (!set[firstThreeChars]) {
                            set[firstThreeChars] = '' + convertToWMI(firstThreeChars);
                        }
                    }
                })
            }
        }
        let wmis: string = '';
        const keys = Object.keys(set);
        keys.forEach((key: string, index: number) => {
            if (index > 0) {
                wmis += ", ";
            }
            wmis += set[key];
        })
        return wmis;
    }

    const convertMultilineStringIntoHTML = (data: string): JSX.Element => {
        const maxLinesToDisplay = 10;
        const dataArray: Array<string> = data.split('\n');
        let retVal = '';
        const lines = dataArray.length;
        const maxLines = Math.min(maxLinesToDisplay, lines);
        for (let i = 0; i < maxLines; i++) {
            if (i === 0) {
                retVal = '<em>' + dataArray[i] + '</em>';
            } else if (i > 0) {
                retVal += '<br />' + dataArray[i];
            }
        }
        if (lines > maxLines) {
            retVal += '<br />...';
        }
        return (<Interweave content={retVal} />)
    }

    /**
     * Routine parses the data string on newlines and counts how many lines of data there are which have content.  It does not
     * count blank lines.
     * @param data : string - The data to parse.
     * @returns number      - The number of non-blank lines.
     */
    const getNumLines = (data: string): number => {
        if (vinFileData) {
            const dataArray: Array<string> = vinFileData.split('\n');
            const b: string = dataArray.reduce((pV: any, cV: any, index: number) => {
                if (index === 1) {
                    return ((cV.trim().length > 0) ? 1 : 0) + ((pV.trim().length > 0) ? 1 : 0)
                } else {
                    return pV + ((cV.trim().length > 0) ? 1 : 0)
                }
            });
            return (Number(b));
        }
        return 0;
    }

    useEffect(() => {
        if (vinFileData) {
            const numberOfNonBlankLines = getNumLines(vinFileData);
            setNumLines(numberOfNonBlankLines);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vinFileData])

    const handleUpload = async (event: any) => {
        setError(null);
        event.preventDefault()

        if (selectedVINFileName && rawVinFileData) {
            setUploading(true);
            const apiService = new APIService();
            apiService.uploadVINFile(rawVinFileData)
                .then((data: any) => {
                    const guid = data.substring(0, data.indexOf('.csv'));
                    setSelectedVINFileGUID(guid);
                    setUploading(false);
                })
                .catch((error: any) => {
                    setAlertDialogContent({
                        title: 'Problem Encountered',
                        body: (<>
                            Error occurred while attempting to upload VIN file:<br></br><br></br>
                            {error.message}
                        </>),
                        yesText: 'Ok',
                        noText: '',
                        yesCallback: () => {
                            setUploading(false);
                            setShowAlert(false)
                        },
                        noCallback: () => {
                            setUploading(false);
                            setShowAlert(false)
                        }
                    });
                    setShowAlert(true);
                })
        }
    }

    return (
        <>
            {!readOnly ? (
                <div className={cx('root')}>
                    <DragAndDrop handleDrop={handleDrop}>
                        <form onSubmit={handleUpload} >
                            <>Use the file selection button or drag a CSV-Formatted VIN File here.<br /><br /></>
                            <div className={cx('root-select-file')}>
                                <input
                                    accept="text/csv"
                                    style={{ display: 'none' }}
                                    id="raised-button-file"
                                    type="file"
                                    ref={(ref) => refFileInput = ref}
                                    onChange={handleChange}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setSelectedVINFileName('');
                                        setSelectedVINFileGUID('');
                                        setSelectFileEnabled(false);
                                        setTimeout(() => {
                                            setSelectFileEnabled(true);
                                        }, 3000)
                                    }}
                                />
                                <label htmlFor="raised-button-file">
                                    <Button variant="contained" component="div" disabled={!selectFileEnabled || uploading}>
                                        Select File
                                        {!selectFileEnabled && <CircularProgress className={cx('circularProgress')} size={20} />}
                                    </Button>
                                </label>

                                {!!selectedVINFileName ? (
                                    <>
                                        <Tooltip title={selectedVINFileName} arrow enterDelay={1000}>
                                            <div className={cx('filename')}>{selectedVINFileName}</div>
                                        </Tooltip>
                                    </>
                                ) : (
                                    <div className={cx('filename')}>{selectedVINFileName}</div>
                                )}
                            </div>

                            <div className={cx('root-2nd-line')}>
                                {file?.name && !vinFileError &&
                                    <Tooltip title={convertMultilineStringIntoHTML(vinFileData)} arrow enterDelay={100}>
                                        <div className={cx('num-lines')}>
                                            Number of non-blank lines: {numLines}
                                        </div>
                                    </Tooltip>
                                }
                                {file?.name && vinFileError &&
                                    <Tooltip title={convertMultilineStringIntoHTML(vinFileData)} arrow enterDelay={100}>
                                        <div className={cx('num-lines error')}>
                                            {vinFileError}
                                        </div>
                                    </Tooltip>
                                }
                            </div>

                            <div className={cx('root-3rd-line')}>
                                <Button variant="contained"
                                    component="div"
                                    className={cx('submit')}
                                    onClick={(e: any) => handleUpload(e)}
                                    disabled={!!vinFileError || uploading || !selectedVINFileName || (!!selectedVINFileGUID && (selectedVINFileGUID.length > 0))}>
                                    Upload
                                </Button>
                            </div>
                        </form>
                        {uploading &&
                            <div className={cx('uploading')}><hr />Uploading...<hr /></div>
                        }
                        {error &&
                            <p>Error - Asset might already exist.</p>
                        }
                        <DisplayReadOnlyField label={'VIN File GUID'} value={selectedVINFileGUID ? selectedVINFileGUID : ''} required={true} />
                    </DragAndDrop>
                </div>
            ) : (
                <div>
                    <DisplayReadOnlyField label={'VIN File Name'} value={selectedVINFileName} required={true} />
                    <DisplayReadOnlyField label={'VIN File GUID'} value={selectedVINFileGUID} required={true} />
                </div>
            )
            }
            <AlertDialog showAlert={showAlert}
                setShowAlert={setShowAlert}
                title={alertDialogContent?.title ? alertDialogContent?.title : ''}
                body={alertDialogContent?.body ? alertDialogContent?.body : ''}
                agree={alertDialogContent?.yesText ? alertDialogContent?.yesText : ''}
                disagree={alertDialogContent?.noText ? alertDialogContent?.noText : ''}
                handleAgree={() => {
                    setShowAlert(false);
                    alertDialogContent?.yesCallback(true);
                }}
                handleDisagree={() => {
                    setShowAlert(false);
                    alertDialogContent?.noCallback(false);
                }} />

        </>
    )
}
